import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus, Plus, Users } from "lucide-react";
import Logo from "@/components/Logo";

const FEATURES = [
  "AI CFO",
  "Financial Dashboard",
  "Cash-Flow Intelligence",
  "Budgeting",
  "Forecasting",
  "Financial Reports",
  "Scenario Planning",
  "Company Financial Memory",
  "Team Collaboration",
  "Financial Alerts",
  "Decision Intelligence",
  "Capital & Runway Intelligence",
  "Integrations (Stripe · Resend · Sri)",
  "Unlimited financial questions",
];

const FAQ = [
  {
    q: "How much does Lemonade cost?",
    a: "Your first 5 seats are always free. After that, CHF 20 per additional seat per month. That's it — no tiers, no lock-in, no hidden fees.",
  },
  {
    q: "What happens when I add my sixth user?",
    a: "You'll see a clear preview: 5 free + 1 paid = CHF 20/month. Nothing is charged until you explicitly confirm. You can always remove seats to drop back to free.",
  },
  {
    q: "Do free users get the AI CFO?",
    a: "Yes. Every feature — AI CFO, forecasting, cash-flow intelligence, integrations — is available to every seat, free or paid. We don't cripple the free tier to force upgrades.",
  },
  {
    q: "What happens if I remove users?",
    a: "Billing automatically adjusts on the next cycle. Drop from 10 seats to 7? You'll pay CHF 40/month instead of CHF 100. Your workspace and history stay intact.",
  },
  {
    q: "What if my team returns to five users?",
    a: "You go back to CHF 0/month. Full stop. Lemonade only makes money when your team grows past five.",
  },
  {
    q: "Is there an annual contract?",
    a: "No. Monthly billing by default. You can pay annually if you want, but nothing forces you into long-term commitments.",
  },
  {
    q: "Are there hidden platform fees?",
    a: "No setup fees, no per-invoice fees, no data-export fees. What you see on this page is what you pay.",
  },
];

export default function Pricing() {
  const [teamSize, setTeamSize] = useState(10);

  const { paidSeats, freeSeats, monthlyPrice, annualPrice } = useMemo(() => {
    const size = Math.max(1, Math.min(500, Number(teamSize) || 1));
    const paid = Math.max(size - 5, 0);
    const free = Math.min(size, 5);
    const monthly = paid * 20;
    return { paidSeats: paid, freeSeats: free, monthlyPrice: monthly, annualPrice: monthly * 12 };
  }, [teamSize]);

  const clamp = (n) => Math.max(1, Math.min(500, n));

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-12">
        <Link to="/"><Logo /></Link>
        <Link
          to="/signup"
          data-testid="pricing-nav-signup"
          className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background hover:bg-foreground/90"
        >
          Start free <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </header>

      <main className="mx-auto max-w-7xl px-6 pb-28 pt-10 md:px-12 md:pt-20">
        {/* Hero */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Pricing</p>
          <h1 className="mt-4 font-display text-5xl leading-[0.95] tracking-tight md:text-7xl">
            Simple Pricing.
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
            Your first <b className="text-foreground">5 seats are free</b>. Then CHF 20 per additional seat per month. No tiers. No lock-in.
          </p>
        </div>

        {/* Single pricing card */}
        <div className="mx-auto mt-16 max-w-4xl">
          <div className="relative overflow-hidden rounded-[36px] border border-border bg-primary p-10 md:p-14" data-testid="pricing-card">
            <div className="grain" aria-hidden></div>
            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-foreground/60">Lemonade</div>
                <div className="mt-3 font-display text-[3.2rem] leading-none tracking-tight text-foreground md:text-[4rem]">
                  5 seats
                  <br />
                  <span className="italic text-foreground/80">free.</span>
                </div>
                <div className="mt-8 flex items-baseline gap-3">
                  <span className="text-sm uppercase tracking-[0.22em] text-foreground/60">Then</span>
                  <span className="font-display text-5xl tracking-tight text-foreground">CHF 20</span>
                </div>
                <div className="mt-1 text-sm text-foreground/70">per additional seat / month</div>
                <Link
                  to="/signup"
                  data-testid="pricing-signup"
                  className="mt-10 inline-flex items-center gap-2 rounded-full bg-foreground px-7 py-4 text-base font-medium text-background hover:bg-foreground/90 active:scale-95"
                >
                  Start Free <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <ul className="grid grid-cols-1 gap-3 text-sm md:grid-cols-1" data-testid="pricing-features">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-primary">
                      <Check className="h-3 w-3" />
                    </span>
                    <span className="text-foreground/85">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Interactive calculator */}
        <div className="mx-auto mt-24 max-w-4xl">
          <div className="mb-8 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Calculator</p>
            <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">How big is your team?</h2>
          </div>

          <div className="rounded-[32px] border border-border bg-white p-8 md:p-12" data-testid="pricing-calculator">
            <div className="flex flex-col items-stretch gap-6 md:flex-row md:items-center md:gap-8">
              <div className="flex-1">
                <div className="mb-3 flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  <Users className="h-3.5 w-3.5" /> Team size
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setTeamSize((s) => clamp(Number(s) - 1))}
                    data-testid="calc-decrement"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-foreground hover:bg-accent"
                    aria-label="Fewer seats"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={teamSize}
                    onChange={(e) => setTeamSize(clamp(Number(e.target.value)))}
                    data-testid="calc-input"
                    className="w-24 border-b-2 border-border bg-transparent py-2 text-center font-display text-4xl tracking-tight tabular-nums outline-none focus:border-primary"
                  />
                  <button
                    onClick={() => setTeamSize((s) => clamp(Number(s) + 1))}
                    data-testid="calc-increment"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-foreground hover:bg-accent"
                    aria-label="More seats"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="ml-2 text-sm text-muted-foreground">people</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="100"
                  step="1"
                  value={Math.min(teamSize, 100)}
                  onChange={(e) => setTeamSize(clamp(Number(e.target.value)))}
                  data-testid="calc-slider"
                  className="mt-5 w-full accent-primary"
                />
                <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  <span>1</span>
                  <span>100</span>
                </div>
              </div>

              <div className="hidden h-32 w-px bg-border md:block" />

              <div className="flex-1 grid grid-cols-2 gap-6" data-testid="calc-summary">
                <Stat label="Free seats" value={freeSeats} testid="calc-free" />
                <Stat label="Paid seats" value={paidSeats} testid="calc-paid" />
                <Stat
                  label="Monthly price"
                  value={monthlyPrice === 0 ? "CHF 0" : `CHF ${monthlyPrice.toLocaleString()}`}
                  strong
                  testid="calc-monthly"
                />
                <Stat
                  label="Annual price"
                  value={annualPrice === 0 ? "CHF 0" : `CHF ${annualPrice.toLocaleString()}`}
                  testid="calc-annual"
                />
              </div>
            </div>
            {teamSize <= 5 && (
              <div className="mt-8 rounded-2xl bg-primary/15 px-5 py-4 text-sm text-foreground" data-testid="calc-free-msg">
                Your entire team uses Lemonade free. 🍋
              </div>
            )}
            {teamSize > 5 && (
              <div className="mt-8 rounded-2xl border border-border bg-[hsl(0_0%_98.5%)] px-5 py-4 text-sm text-muted-foreground">
                {paidSeats} × CHF 20 = <b className="font-mono text-foreground">CHF {monthlyPrice.toLocaleString()}</b> per month. Cancel or downsize anytime.
              </div>
            )}
          </div>
        </div>

        {/* FAQ */}
        <div className="mx-auto mt-24 max-w-3xl" data-testid="pricing-faq">
          <div className="mb-10 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">FAQ</p>
            <h2 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">Straight answers.</h2>
          </div>
          <div className="divide-y divide-border rounded-[28px] border border-border bg-white">
            {FAQ.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} tid={`faq-${i}`} />
            ))}
          </div>
        </div>

        {/* Closing */}
        <div className="mx-auto mt-24 max-w-3xl text-center">
          <h3 className="font-display text-3xl tracking-tight md:text-4xl">
            Serious financial intelligence,
            <br />
            <span className="italic text-foreground/60">without worrying about software costs.</span>
          </h3>
          <p className="mt-6 text-muted-foreground">
            Lemonade grows with you. When your team grows, we grow. That's it.
          </p>
          <Link
            to="/signup"
            data-testid="pricing-cta-bottom"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-medium text-primary-foreground hover:bg-primary/90 active:scale-95"
          >
            Start free — 5 seats included <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, strong = false, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      <div className={`mt-1.5 tabular-nums tracking-tight ${strong ? "font-display text-3xl" : "font-display text-2xl"}`}>
        {value}
      </div>
    </div>
  );
}

function FaqItem({ q, a, tid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid={tid}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left md:px-8"
      >
        <span className="text-base font-medium">{q}</span>
        <Plus className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-45" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground md:px-8" data-testid={`${tid}-answer`}>
          {a}
        </div>
      )}
    </div>
  );
}
